import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Users, Mail, Key, Trash2, Edit, Building2, MapPin, CreditCard, UserPlus, Percent, Disc3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArtistManagement = ({ user }) => {
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedArtistForMembers, setSelectedArtistForMembers] = useState(null);
  const [bandMembers, setBandMembers] = useState([]);
  const [groupEmail, setGroupEmail] = useState('');
  const [memberFormData, setMemberFormData] = useState({
    name: '',
    email: '',
    role: '',
  });
  const [artistSettings, setArtistSettings] = useState({
    company_name: '',
    vat_number: '',
    company_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    country: '',
    email: '',
    phone: '',
    bank_name: '',
    account_number: '',
    sort_code: '',
    bic_code: '',
    swift_code: '',
    payment_notes: '',
    has_agent: true,
    agent_commission_rate: 10,
    is_label_artist: false,
    label_profit_split: 50,
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      toast.error('Failed to fetch artists');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        role: 'artist'
      };
      
      await axios.post(`${API}/artists/create`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(`Artist ${formData.name} created successfully!`);
      setOpen(false);
      setFormData({ name: '', email: '', password: '' });
      fetchArtists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create artist');
    }
  };

  const handleEditArtist = async (artist) => {
    setEditingArtist(artist);
    
    // Fetch artist's settings
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings?user_id=${artist.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data) {
        setArtistSettings(response.data);
      } else {
        // No settings yet, use defaults
        setArtistSettings({
          company_name: artist.name,
          vat_number: '',
          company_number: '',
          address_line1: '',
          address_line2: '',
          city: '',
          postcode: '',
          country: '',
          email: artist.email,
          phone: '',
          bank_name: '',
          account_number: '',
          sort_code: '',
          bic_code: '',
          swift_code: '',
          payment_notes: '',
        });
      }
      setEditDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch artist settings');
    }
  };

  const saveArtistSettings = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // Save settings
      await axios.post(`${API}/settings/artist/${editingArtist.id}`, artistSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // If password was provided, update it
      if (artistSettings.new_password && artistSettings.new_password.trim()) {
        await axios.post(`${API}/artists/${editingArtist.id}/password`, 
          { password: artistSettings.new_password },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Settings and password updated for ${editingArtist.name}!`);
      } else {
        toast.success(`Settings saved for ${editingArtist.name}!`);
      }
      
      setEditDialogOpen(false);
      setEditingArtist(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const deleteArtist = async (artistId, artistName) => {
    if (!window.confirm(`Are you sure you want to delete ${artistName}? This will also delete all their data (tasks, shows, files, etc.).`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/artists/${artistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Artist ${artistName} deleted`);
      fetchArtists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete artist');
    }
  };

  const handleManageMembers = async (artist) => {
    setSelectedArtistForMembers(artist);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists/${artist.id}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setBandMembers(response.data.members || []);
      setGroupEmail(response.data.group_email || '');
    } catch (error) {
      // No members found - start fresh
      setBandMembers([]);
      setGroupEmail('');
    }
    
    setMembersDialogOpen(true);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (!memberFormData.name || !memberFormData.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/artists/${selectedArtistForMembers.id}/members`,
        memberFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Member added successfully!');
      setMemberFormData({ name: '', email: '', role: '' });
      
      // Refresh members
      const response = await axios.get(`${API}/artists/${selectedArtistForMembers.id}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBandMembers(response.data.members || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Delete this band member?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/artists/${selectedArtistForMembers.id}/members/${memberId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Member deleted!');
      
      // Refresh members
      const response = await axios.get(`${API}/artists/${selectedArtistForMembers.id}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBandMembers(response.data.members || []);
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  const handleSaveGroupEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/artists/${selectedArtistForMembers.id}/group-email`,
        { group_email: groupEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Group email saved!');
    } catch (error) {
      toast.error('Failed to save group email');
    }
  };

  // Both managers and admins can access artist management
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  
  if (!isManagerOrAdmin) {
    return (
      <div className="space-y-8">
        <Card className="bg-card border-border/50 p-12 text-center">
          <p className="text-muted-foreground">Only managers and admins can access artist management.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="artist-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-2xl text-foreground">Artist Management</h1>
          <p className="text-muted-foreground text-sm mt-1">Add and manage your artists</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
              data-testid="add-artist-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Artist
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border/50">
            <DialogHeader>
              <DialogTitle className="font-manrope font-bold text-2xl">Create New Artist Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Artist Name</Label>
                <Input
                  id="name"
                  data-testid="artist-name-input"
                  placeholder="e.g., Porij"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="artist-email-input"
                  placeholder="artist@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Initial Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="artist-password-input"
                  placeholder="Set a password for the artist"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="bg-input/50 border-border rounded-xl"
                />
                <p className="text-xs text-muted-foreground">The artist can change this later</p>
              </div>
              <Button
                type="submit"
                data-testid="submit-artist-button"
                className="w-full bg-primary hover:bg-primary/90"
              >
                Create Artist Account
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Artist Details Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border/50 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-manrope font-bold text-2xl">
              Edit Artist Details - {editingArtist?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <form onSubmit={saveArtistSettings} className="space-y-6 pb-4">
              {/* Agent Fee Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Percent className="w-5 h-5" />
                  Booking Agent Settings
                </h3>
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Has Booking Agent</Label>
                      <p className="text-sm text-muted-foreground">Does this artist have an agent who takes a commission on shows?</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setArtistSettings({ ...artistSettings, has_agent: !artistSettings.has_agent })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        artistSettings.has_agent ? 'bg-primary' : 'bg-gray-500'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          artistSettings.has_agent ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {artistSettings.has_agent && (
                    <div className="space-y-2">
                      <Label>Agent Commission Rate (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={artistSettings.agent_commission_rate}
                        onChange={(e) => setArtistSettings({ ...artistSettings, agent_commission_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="10"
                        className="bg-input/50 border-border rounded-xl w-32"
                      />
                      <p className="text-xs text-muted-foreground">This percentage will be deducted from show fees to calculate net profit</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Record Label Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Disc3 className="w-5 h-5" />
                  Record Label
                </h3>
                <div className="p-4 bg-muted/30 rounded-xl border border-border/50 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Label Artist</Label>
                      <p className="text-sm text-muted-foreground">Is this artist signed to the Underplay record label?</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setArtistSettings({ ...artistSettings, is_label_artist: !artistSettings.is_label_artist })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        artistSettings.is_label_artist ? 'bg-primary' : 'bg-gray-500'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          artistSettings.is_label_artist ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {artistSettings.is_label_artist && (
                    <div className="space-y-2">
                      <Label>Artist Profit Split (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={artistSettings.label_profit_split}
                        onChange={(e) => setArtistSettings({ ...artistSettings, label_profit_split: parseFloat(e.target.value) || 50 })}
                        placeholder="50"
                        className="bg-input/50 border-border rounded-xl w-32"
                      />
                      <p className="text-xs text-muted-foreground">Artist&apos;s share of profit after costs are recouped (label gets the remainder)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Company Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={artistSettings.company_name}
                      onChange={(e) => setArtistSettings({ ...artistSettings, company_name: e.target.value })}
                      placeholder="Artist/Company Name"
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>VAT Number</Label>
                    <Input
                      value={artistSettings.vat_number}
                      onChange={(e) => setArtistSettings({ ...artistSettings, vat_number: e.target.value })}
                      placeholder="GB123456789"
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Number</Label>
                    <Input
                      value={artistSettings.company_number}
                      onChange={(e) => setArtistSettings({ ...artistSettings, company_number: e.target.value })}
                      placeholder="12345678"
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Address
                </h3>
                <div className="space-y-3">
                  <Input
                    value={artistSettings.address_line1}
                    onChange={(e) => setArtistSettings({ ...artistSettings, address_line1: e.target.value })}
                    placeholder="Address Line 1"
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <Input
                    value={artistSettings.address_line2}
                    onChange={(e) => setArtistSettings({ ...artistSettings, address_line2: e.target.value })}
                    placeholder="Address Line 2 (optional)"
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      value={artistSettings.city}
                      onChange={(e) => setArtistSettings({ ...artistSettings, city: e.target.value })}
                      placeholder="City"
                      className="bg-input/50 border-border rounded-xl"
                    />
                    <Input
                      value={artistSettings.postcode}
                      onChange={(e) => setArtistSettings({ ...artistSettings, postcode: e.target.value })}
                      placeholder="Postcode"
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>
                  <Input
                    value={artistSettings.country}
                    onChange={(e) => setArtistSettings({ ...artistSettings, country: e.target.value })}
                    placeholder="Country"
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="email"
                    value={artistSettings.email}
                    onChange={(e) => setArtistSettings({ ...artistSettings, email: e.target.value })}
                    placeholder="Email"
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <Input
                    value={artistSettings.phone}
                    onChange={(e) => setArtistSettings({ ...artistSettings, phone: e.target.value })}
                    placeholder="Phone"
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
              </div>

              {/* Password Reset */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Login Password
                </h3>
                <div className="space-y-2">
                  <Label>Set New Password (optional)</Label>
                  <Input
                    type="text"
                    value={artistSettings.new_password || ''}
                    onChange={(e) => setArtistSettings({ ...artistSettings, new_password: e.target.value })}
                    placeholder="Leave blank to keep current password"
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter a new password here to reset the artist&apos;s login password. You can then copy and send this to them.
                  </p>
                </div>
              </div>

              {/* Payment Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Details
                </h3>
                <Input
                  value={artistSettings.bank_name}
                  onChange={(e) => setArtistSettings({ ...artistSettings, bank_name: e.target.value })}
                  placeholder="Bank Name"
                  className="bg-input/50 border-border rounded-xl"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    value={artistSettings.sort_code}
                    onChange={(e) => setArtistSettings({ ...artistSettings, sort_code: e.target.value })}
                    placeholder="Sort Code"
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <Input
                    value={artistSettings.account_number}
                    onChange={(e) => setArtistSettings({ ...artistSettings, account_number: e.target.value })}
                    placeholder="Account Number"
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    value={artistSettings.bic_code}
                    onChange={(e) => setArtistSettings({ ...artistSettings, bic_code: e.target.value })}
                    placeholder="BIC Code (e.g., BARCGB22)"
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <Input
                    value={artistSettings.swift_code}
                    onChange={(e) => setArtistSettings({ ...artistSettings, swift_code: e.target.value })}
                    placeholder="SWIFT Code (e.g., BARCGB22XXX)"
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Notes</Label>
                  <Input
                    value={artistSettings.payment_notes}
                    onChange={(e) => setArtistSettings({ ...artistSettings, payment_notes: e.target.value })}
                    placeholder="e.g., Please pay within 30 days"
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  Save Artist Details
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary Card - Compact */}
      <Card className="bg-card border-border/50 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Artists</p>
            <p className="font-manrope font-bold text-xl text-foreground">{artists.length}</p>
          </div>
        </div>
      </Card>

      {/* Artists List - Reduced Gap */}
      <div className="grid gap-3">
        {artists.length === 0 ? (
          <Card className="bg-card border-border/50 p-12 text-center">
            <p className="text-muted-foreground">No artists yet. Add your first artist to get started!</p>
          </Card>
        ) : (
          artists.map((artist) => (
            <Card
              key={artist.id}
              className="bg-card border-border/50 p-4 hover:border-primary/50 transition-colors"
              data-testid={`artist-item-${artist.id}`}
            >
              <div className="flex flex-col">
                {/* Top: Artist info with Edit button */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold text-lg flex-shrink-0">
                      {artist.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-manrope font-bold text-base text-foreground truncate">{artist.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{artist.email}</span>
                      </div>
                    </div>
                  </div>

                  {/* Edit and Members buttons on top right */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageMembers(artist)}
                      className="h-8 px-3 text-xs touch-none flex-shrink-0"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      Members
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditArtist(artist)}
                      className="h-8 px-3 text-xs touch-none flex-shrink-0"
                      data-testid={`edit-artist-${artist.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>

                {/* Bottom: Delete button on separate line */}
                <div className="flex justify-end pt-3 border-t border-border/30">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteArtist(artist.id, artist.name);
                    }}
                    className="min-w-[90px] h-8 text-xs touch-none"
                    data-testid={`delete-artist-${artist.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Band Members Dialog */}
      <Dialog open={membersDialogOpen} onOpenChange={setMembersDialogOpen}>
        <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-2xl">
              Band Members - {selectedArtistForMembers?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto max-h-[70vh] pr-2">
            {/* Group Email Section */}
            <div className="space-y-3 p-4 bg-accent/20 rounded-xl border border-border/50">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Group Email (for whole band)
              </h3>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="band@example.com"
                  value={groupEmail}
                  onChange={(e) => setGroupEmail(e.target.value)}
                  className="bg-input/50 border-border rounded-xl flex-1"
                />
                <Button
                  onClick={handleSaveGroupEmail}
                  variant="outline"
                  size="sm"
                >
                  Save
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                This email will receive all band-related communications
              </p>
            </div>

            {/* Add Member Form */}
            <div className="space-y-3 p-4 bg-primary/5 rounded-xl border border-primary/20">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Add Band Member
              </h3>
              <form onSubmit={handleAddMember} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Member Name *</Label>
                    <Input
                      placeholder="John Doe"
                      value={memberFormData.name}
                      onChange={(e) => setMemberFormData({ ...memberFormData, name: e.target.value })}
                      className="bg-input/50 border-border rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Personal Email *</Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={memberFormData.email}
                      onChange={(e) => setMemberFormData({ ...memberFormData, email: e.target.value })}
                      className="bg-input/50 border-border rounded-xl"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Role (optional)</Label>
                  <Input
                    placeholder="e.g., Lead Vocals, Guitar, Drums"
                    value={memberFormData.role}
                    onChange={(e) => setMemberFormData({ ...memberFormData, role: e.target.value })}
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </form>
            </div>

            {/* Members List */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                Band Members ({bandMembers.length})
              </h3>
              {bandMembers.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm bg-muted/20 rounded-xl">
                  No band members added yet. Add members above to manage individual emails for PRS distributions.
                </div>
              ) : (
                <div className="space-y-2">
                  {bandMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-start justify-between p-3 bg-card border border-border/50 rounded-xl"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{member.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" />
                          {member.email}
                        </p>
                        {member.role && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {member.role}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMember(member.id)}
                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                💡 <strong>Tip:</strong> Add all band members with their individual emails. When sending PRS invoices or statements, you can choose to email each member separately with their portion.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistManagement;
